import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  cover_image_url: string;
  cover_image_alt: string;
  tags: string;
  author_name: string;
  published_at: string;
}

const BlogIndexPage: React.FC = () => {
  useDocumentTitle('Blog & Insights — Analytics, SEO & Monitoring Tips | CloudAyn', false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
        const res = await fetch(`${apiUrl}/v1/public/blog?page=${page}&limit=9`);
        const data = await res.json();
        if (data.status === 'success') {
          setPosts(data.data.posts);
          setTotalPages(data.data.pagination.pages || 1);
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchPosts();
  }, [page]);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Nav */}
      <nav className="navbar navbar-light bg-white border-bottom px-3 py-3">
        <div className="container d-flex justify-content-between align-items-center">
          <Link to="/" className="navbar-brand d-flex align-items-center text-decoration-none">
            <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,#06b6d4,#3b82f6)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800 }}>C</div>
            <span style={{ fontWeight: 800, fontSize: '1.15rem', marginLeft: 8, background: 'linear-gradient(135deg,#06b6d4,#3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>CloudAyn</span>
          </Link>
          <div className="d-flex gap-2">
            <Link to="/login" className="btn btn-outline-primary btn-sm">Login</Link>
            <Link to="/signup" className="btn btn-primary btn-sm">Get Started Free</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="py-5 text-center" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)', color: '#fff' }}>
        <div className="container">
          <h1 className="fw-bold mb-2" style={{ fontSize: '2.2rem' }}>Blog &amp; Insights</h1>
          <p style={{ color: '#94a3b8', maxWidth: 560, margin: '0 auto' }}>
            Guides, tips and product updates on website analytics, uptime monitoring, AI SEO tools, and security
          </p>
        </div>
      </div>

      {/* Posts grid */}
      <div className="container py-5">
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-file-earmark-text" style={{ fontSize: 48, color: '#cbd5e1' }}></i>
            <p className="mt-3 text-muted">No posts published yet. Check back soon!</p>
            <Link to="/" className="btn btn-outline-primary mt-2">Back to Home</Link>
          </div>
        ) : (
          <>
            <div className="row g-4">
              {posts.map((post) => (
                <div key={post.id} className="col-lg-4 col-md-6">
                  <Link to={`/blog/${post.slug}`} className="text-decoration-none">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16, overflow: 'hidden', transition: 'transform 0.2s', cursor: 'pointer' }}
                      onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-4px)')}
                      onMouseLeave={e => (e.currentTarget.style.transform = 'none')}>
                      {post.cover_image_url ? (
                        <img src={post.cover_image_url} alt={post.cover_image_alt || post.title} style={{ width: '100%', height: 200, objectFit: 'cover' }} loading="lazy" />
                      ) : (
                        <div style={{ width: '100%', height: 200, background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, color: '#94a3b8' }}>
                          <i className="bi bi-file-earmark-text"></i>
                        </div>
                      )}
                      <div className="card-body">
                        {post.tags && (
                          <div className="d-flex gap-1 flex-wrap mb-2">
                            {post.tags.split(',').slice(0, 2).map(t => (
                              <span key={t} className="badge" style={{ background: '#eff6ff', color: '#3b82f6', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em' }}>{t.trim()}</span>
                            ))}
                          </div>
                        )}
                        <h2 className="h6 fw-bold mb-2" style={{ color: '#0f172a', lineHeight: 1.4 }}>{post.title}</h2>
                        {post.excerpt && <p className="small text-muted mb-3" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.excerpt}</p>}
                        <div className="d-flex justify-content-between" style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          <span>{post.author_name}</span>
                          {post.published_at && <span>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-center gap-2 mt-5">
                <button className="btn btn-outline-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Previous</button>
                <button className="btn btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="border-top py-4 bg-white text-center">
        <p className="text-muted small mb-1">&copy; 2026 CloudAyn by INFIYO TECHNOLOGIES WLL</p>
        <div className="d-flex justify-content-center gap-3">
          <Link to="/" className="text-muted small text-decoration-none">Home</Link>
          <Link to="/signup" className="text-muted small text-decoration-none">Get Started Free</Link>
          <Link to="/privacy" className="text-muted small text-decoration-none">Privacy</Link>
          <Link to="/terms" className="text-muted small text-decoration-none">Terms</Link>
        </div>
      </footer>
    </div>
  );
};

export default BlogIndexPage;
